import { isRequired } from 'Automations/nodeDefinitions/validation/validators'
import { allHandlesConnected } from 'Automations/nodeDefinitions/validation/nodeValidators'
import type { AutomationsPresentationV1Node } from 'Automations/automationEditor/types'
import type { ConditionalProperties } from 'Automations/nodeDefinitions/conditions'
import type { ValidatorReturn } from 'Automations/nodeDefinitions/validation'
import type { SwitchGroup } from './switch.types'

export const conditionPropertyRequired = isRequired({
  title: 'Incomplete condition',
  message: 'Property required'
})

export const conditionOperatorRequired = (
  value: unknown,
  context?: Record<string, unknown>
) => {
  if (!context?.property) return undefined
  return value
    ? undefined
    : { title: 'Incomplete condition', message: 'Operator required' }
}

export const conditionValueRequired = (
  value: unknown,
  context?: Record<string, unknown>,
  scope?: Record<string, unknown>
): ValidatorReturn | ValidatorReturn[] | undefined => {
  if (!context?.property) return undefined

  const property = context.property as string
  const operatorKey = context.operator as string | undefined
  const allProperties = scope?.allProperties as
    | ConditionalProperties
    | undefined
  const groups = (scope?.groups ?? []) as SwitchGroup[]
  const conditionId = context.id as string | undefined
  const siblingConditions = conditionId
    ? (groups
        .find(group =>
          group.conditions.some(condition => condition.id === conditionId)
        )
        ?.conditions.filter(condition => condition.id !== conditionId) ?? [])
    : []

  const nodes = (scope?.nodes ?? []) as AutomationsPresentationV1Node[]
  const downstreamNodes = (scope?.downstreamNodes ??
    []) as AutomationsPresentationV1Node[]

  const propertyDef = allProperties?.[property]
  const operatorDef =
    operatorKey && propertyDef ? propertyDef.operators[operatorKey] : undefined

  const results: ValidatorReturn[] = []

  if (propertyDef?.validateProperty && operatorKey) {
    const propertyResult = propertyDef.validateProperty({
      operator: operatorKey,
      value,
      siblingConditions,
      nodes,
      downstreamNodes
    })
    if (propertyResult) {
      results.push(
        ...(Array.isArray(propertyResult) ? propertyResult : [propertyResult])
      )
    }
  }

  if (operatorDef?.validateValue) {
    const operatorResult = operatorDef.validateValue({ value })
    if (operatorResult) {
      results.push(
        ...(Array.isArray(operatorResult) ? operatorResult : [operatorResult])
      )
    }
  }

  if (results.length > 0) return results

  // Neither hook is defined for this operator — fall back to the generic
  // "value required" check.
  if (propertyDef?.validateProperty || operatorDef?.validateValue) {
    return undefined
  }

  return isValueEmpty(value)
    ? { title: 'Incomplete condition', message: 'Value required' }
    : undefined
}

// Generic fallback when the operator doesn't declare its own `validateValue`.
// Operators store primitives, arrays, or `{ amount, unit }` for
// amount-with-unit comparisons; this predicate covers each baseline shape.
const isValueEmpty = (value: unknown): boolean => {
  if (value === undefined || value === null || value === '') return true
  if (Array.isArray(value)) return value.length === 0
  if (typeof value === 'object' && value !== null && 'amount' in value) {
    const { amount, unit } = value as {
      amount: unknown
      unit?: unknown
    }
    return isBlank(amount) || isBlank(unit)
  }
  return false
}

const isBlank = (v: unknown): boolean =>
  v === undefined || v === null || v === ''

export const handlesConnected = allHandlesConnected({
  handleTypes: ['target', 'source'],
  title: 'Disconnected node',
  message: 'All handles must be connected'
})
